import { ImageGravity } from "common/components/image-types";
import type { ImageUrlOptions } from "common/components/image-url-options";
import { massageImageUrl } from "common/components/portable-renderers";
import type { MinimalAppEnvironment, UploadSession } from "common/components/types";
import { isUploadFileResponseError } from "common/components/types";
import { getNamedIcon } from "common/named-icons";
import { defined } from "common/Support";
import * as React from "react";
import { withTheme } from "styled-components";
import { showCancelUploadModal, showUploadFailedRetryModal } from "webapp/lib/in-app-modals";
import { getFirstFileFromInput, readFileAsURL } from "../../../lib/file-utils";
import type { RuntimeTheme } from "../../../lib/theme/theme";
import Img from "../img/img";
import { UserProfileImage } from "./user-profile-image";
import {
    UserProfileImageSize,
    UserProfileMadeWithGlide,
    UserProfileStyle,
    UserProfileSubtitle,
    UserProfileTextContainer,
    UserProfileTitle,
} from "./user-profile-styles";

export { UserProfileImageSize } from "./user-profile-styles";

interface Props {
    readonly theme: RuntimeTheme;
    readonly appEnvironment: MinimalAppEnvironment;
    readonly showBranding?: boolean;
    readonly title?: string;
    readonly subtitle?: string;
    readonly imageURL?: string;
    readonly onImageChange?: (newImageURL: string) => void;
    readonly canUploadImage?: boolean;
    readonly onClick?: React.MouseEventHandler<HTMLDivElement>;
}

interface State {
    uploadProgress: number | undefined;
    sourceOverride: string | undefined;
}

class UserProfile extends React.PureComponent<Props, State> {
    public state: State = { uploadProgress: undefined, sourceOverride: undefined };
    private uploadSession: UploadSession | undefined;

    private setUploadProgress = (total: number, current: number) => {
        this.setState({ uploadProgress: (current * 100) / total });
    };

    private setSourceOverride(orig: string | undefined, replacement: string) {
        const { sourceOverride } = this.state;
        if (sourceOverride !== undefined && orig !== sourceOverride) return;
        this.setState({ sourceOverride: replacement });
    }

    private clearSourceOverride = (replacement: string) => {
        const { sourceOverride } = this.state;
        if (sourceOverride !== replacement) return;
        this.setState({ sourceOverride: undefined });
    };

    private onInputChange = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const firstFile = getFirstFileFromInput(ev);
        if (firstFile === undefined) return;

        const { onImageChange, appEnvironment, imageURL } = this.props;
        if (onImageChange === undefined || appEnvironment.uploadAppFile === undefined) return;

        this.setState({ uploadProgress: 0 });
        const overrideURLPromise = readFileAsURL(firstFile).then(url => {
            this.setSourceOverride(imageURL, url);
            return url;
        });
        void overrideURLPromise;

        if (this.uploadSession !== undefined) {
            this.uploadSession.cancel();
        }

        let uploadURL: string | undefined;
        const session = appEnvironment.uploadAppFile(firstFile, "user-profile", this.setUploadProgress);
        this.uploadSession = session;

        while (true) {
            const response = await this.uploadSession.attempt();
            if (!isUploadFileResponseError(response)) {
                uploadURL = response.path;
                break;
            }
            if (!(await showUploadFailedRetryModal(response, appEnvironment.appKind))) break;
        }

        if (this.uploadSession === session) {
            this.uploadSession = undefined;
        }

        if (uploadURL !== undefined) {
            onImageChange(uploadURL);
            void overrideURLPromise.then(prev => this.setSourceOverride(prev, defined(uploadURL)));
        } else {
            void overrideURLPromise.then(this.clearSourceOverride);
        }

        this.setState({ uploadProgress: undefined });
    };

    private onCancelClick = async (ev: React.MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        ev.preventDefault();

        const shouldCancel = await showCancelUploadModal(this.props.appEnvironment.appKind);
        if (!shouldCancel) return;

        if (this.uploadSession !== undefined) {
            this.uploadSession.cancel();
            this.uploadSession = undefined;
        }
    };

    public componentDidUpdate() {
        const { imageURL } = this.props;
        if (imageURL !== undefined) {
            this.clearSourceOverride(imageURL);
        }
    }

    public componentWillUnmount() {
        // Cancel all pending uploads when the component unmounts.
        // Clearly, we don't care about them anymore.
        // FIXME: We should probably not be relying on React state to
        // do this...
        if (this.uploadSession !== undefined) {
            this.uploadSession.cancel();
            this.uploadSession = undefined;
        }
    }

    public render(): React.ReactNode {
        const { theme, showBranding = false, title, subtitle, imageURL, canUploadImage = true, onClick } = this.props;
        const { uploadProgress, sourceOverride } = this.state;

        const options: ImageUrlOptions = {
            thumbnail: false,
            width: UserProfileImageSize,
            height: UserProfileImageSize,
            gravity: ImageGravity.Center,
        };
        const massagedImageURL = massageImageUrl(sourceOverride ?? imageURL, options);

        const useDefaultImage = massagedImageURL === undefined;
        const image = useDefaultImage ? (
            <svg
                width={UserProfileImageSize}
                height={UserProfileImageSize}
                viewBox="0 0 96 96"
                id="Color"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M48 0C21.504 0 0 21.504 0 48C0 74.496 21.504 96 48 96C74.496 96 96 74.496 96 48C96 21.504 74.496 0 48 0ZM24.336 78.144C26.4 73.824 38.976 69.6 48 69.6C57.024 69.6 69.648 73.824 71.664 78.144C65.136 83.328 56.928 86.4 48 86.4C39.072 86.4 30.864 83.328 24.336 78.144ZM78.528 71.184C71.664 62.832 55.008 60 48 60C40.992 60 24.336 62.832 17.472 71.184C12.576 64.752 9.6 56.736 9.6 48C9.6 26.832 26.832 9.6 48 9.6C69.168 9.6 86.4 26.832 86.4 48C86.4 56.736 83.424 64.752 78.528 71.184ZM48 19.2C38.688 19.2 31.2 26.688 31.2 36C31.2 45.312 38.688 52.8 48 52.8C57.312 52.8 64.8 45.312 64.8 36C64.8 26.688 57.312 19.2 48 19.2ZM48 43.2C44.016 43.2 40.8 39.984 40.8 36C40.8 32.016 44.016 28.8 48 28.8C51.984 28.8 55.2 32.016 55.2 36C55.2 39.984 51.984 43.2 48 43.2Z"
                    fill="currentColor"
                />
            </svg>
        ) : (
            <Img
                className={uploadProgress !== undefined ? "up-uploading" : undefined}
                src={massagedImageURL}
                alt=""
                width={UserProfileImageSize}
                height={UserProfileImageSize}
            />
        );

        const MadeWithGlide = getNamedIcon("madeWithGlide");

        return (
            <UserProfileStyle onClick={onClick}>
                <UserProfileImage
                    image={image}
                    baseUploadColor={theme.primaryAccentColor}
                    secondaryUploadColor={theme.baseColor}
                    onInputChange={canUploadImage ? this.onInputChange : undefined}
                    onCancelClick={this.onCancelClick}
                    uploadProgress={uploadProgress}
                />
                <UserProfileTextContainer>
                    {title !== undefined && <UserProfileTitle>{title}</UserProfileTitle>}
                    {subtitle !== undefined && <UserProfileSubtitle>{subtitle}</UserProfileSubtitle>}
                </UserProfileTextContainer>
                {showBranding && (
                    <UserProfileMadeWithGlide href="https://glideapps.com" rel="noopener noreferrer" target="_blank">
                        <MadeWithGlide fgColor={theme.fgColorOpaque} />
                    </UserProfileMadeWithGlide>
                )}
            </UserProfileStyle>
        );
    }
}

export default withTheme(UserProfile);
